import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

const DIRECTIONS = [['up', 0, -1, '↑'], ['left', -1, 0, '←'], ['right', 1, 0, '→'], ['down', 0, 1, '↓']];
// Gesture timing only. The parent retains geometry, validation and history.
const PositionPad = forwardRef(function PositionPad({ x, y, disabled, contextKey, onStep, onStart, onStop, onReset }, ref) {
  const callbacks = useRef(null);
  callbacks.current = { onStep, onStart, onStop, disabled };
  const hold = useRef(null);
  const swallowClick = useRef(false);
  const clickReset = useRef(null);
  const [direction, setDirection] = useState(null);
  const stop = useCallback(() => {
    const gesture = hold.current;
    if (!gesture) return;
    hold.current = null;
    clearTimeout(gesture.timer);
    clearTimeout(clickReset.current);
    clickReset.current = setTimeout(() => { swallowClick.current = false; }, 0);
    if (gesture.button.hasPointerCapture?.(gesture.pointerId)) gesture.button.releasePointerCapture(gesture.pointerId);
    callbacks.current.onStop();
    setDirection(null);
  }, []);
  useImperativeHandle(ref, () => ({ stop }), [stop]);
  useLayoutEffect(() => {
    const end = () => stop();
    window.addEventListener('blur', end);
    document.addEventListener('visibilitychange', end);
    return () => {
      end();
      clearTimeout(clickReset.current);
      swallowClick.current = false;
      window.removeEventListener('blur', end);
      document.removeEventListener('visibilitychange', end);
    };
  }, [stop, contextKey]);
  useLayoutEffect(() => { if (disabled) stop(); }, [disabled, stop]);
  const start = (button, name, dx, dy, pointerId) => {
    if (hold.current || callbacks.current.disabled) return;
    callbacks.current.onStart();
    const gesture = { button, pointerId, started: Date.now(), timer: null };
    hold.current = gesture;
    setDirection(name);
    callbacks.current.onStep(dx, dy);
    const repeat = () => {
      if (hold.current !== gesture || callbacks.current.disabled) return stop();
      const step = Date.now() - gesture.started >= 1000 ? 3 : 1;
      // Every timer tick must observe the preceding geometry transaction even
      // when React batches timers; otherwise a hold repeats an old offset.
      flushSync(() => callbacks.current.onStep(dx * step, dy * step));
      gesture.timer = setTimeout(repeat, 150);
    };
    gesture.timer = setTimeout(repeat, 150);
  };
  return <div className="dk-position" data-testid="position-pad" data-holding={direction || ''}>
    <div className="dk-style-head dk-position-head">Position
      <button type="button" className="dk-btn ghost sm dk-position-reset" aria-label="Reset position" disabled={disabled || (x === 0 && y === 0)}
        onClick={() => { stop(); onReset(); }}>Reset</button>
    </div>
    <div className="dk-position-pad" role="group" aria-label="Position">
      {DIRECTIONS.map(([name, dx, dy, arrow]) => <button key={name} type="button"
        className={`dk-position-${name}${direction === name ? ' on' : ''}`} aria-label={`Nudge ${name}`}
        disabled={disabled} onPointerDown={event => {
          if (event.button !== 0) return;
          event.preventDefault(); event.currentTarget.focus(); swallowClick.current = true;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          start(event.currentTarget, name, dx, dy, event.pointerId);
        }} onPointerUp={stop} onPointerCancel={stop} onLostPointerCapture={stop} onPointerLeave={stop}
        onPointerMove={event => {
          if (!hold.current) return;
          const r = event.currentTarget.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) stop();
        }} onBlur={stop} onKeyDown={event => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault(); swallowClick.current = true;
          if (!event.repeat) start(event.currentTarget, name, dx, dy);
        }} onKeyUp={event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); stop(); }
        }} onClick={event => {
          if (swallowClick.current) { swallowClick.current = false; return; }
          // Assistive activation can send click without pointer/key events.
          callbacks.current.onStart(); callbacks.current.onStep(dx, dy); callbacks.current.onStop();
        }}><span aria-hidden="true">{arrow}</span></button>)}
    </div>

  </div>;
});
export default PositionPad;
